import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { emailRegex, FormState } from '@opu/utils';
import AxiosInstance from '../../../helpers/AxiosInstance';
import { useTranslation } from 'react-i18next';
import FormData from 'form-data';
import { useHistory } from 'react-router-dom';

const useForm = () => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const [authors, setAuthors] = useState([]);
  const [categories, setCategories] = useState([]);

  const [loading, setLoading] = useState(false);

  const getAuthors = () => {
    setLoading(true);
    AxiosInstance.get('/author')
      .then((res) => {
        setAuthors(res.data.items);
      })
      .catch((error) => {
        Swal.fire(t('author.ERROR_GET_AUTHORS'), '', 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const createAuthor = (name) => {
    setLoading(true);
    AxiosInstance.post('/author', { name: name, display: true })
      .then((res) => {
        Swal.fire(t('author.SUCCESS_CREATE_AUTHOR'), '', 'success');
        getAuthors();
      })
      .catch((error) => {
        if (error.status === 400) {
          handleErrorsServer(error.data.message);
        } else Swal.fire(t('author.ERROR_CREATE_AUTHOR'), '', 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getCategories = () => {
    setLoading(true);
    AxiosInstance.get('/category')
      .then((res) => {
        setCategories(res.data);
      })
      .catch((error) => {
        Swal.fire(t('category.ERROR_GET_CATEGORIES'), '', 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getAuthors();
    getCategories();
  }, []);

  const back = () => {
    history.goBack();
  };

  const createBook = () => {
    setLoading(true);
    const formData = new FormData();
    formData.append('ISBN', formState.inputValues.ISBN);
    formState.inputValues.authors.forEach((tag) => formData.append('authors[]', tag));
    formState.inputValues.categories.forEach((tag) => formData.append('categories[]', tag));
    formData.append('title', formState.inputValues.title);
    formData.append('summary', formState.inputValues.summary);
    formData.append('abstract', formState.inputValues.abstract);
    formData.append('editionNumber', formState.inputValues.editionNumber);
    formData.append('publicationNumber', formState.inputValues.publicationNumber);
    formData.append('publishingHouse', formState.inputValues.publishingHouse);
    formData.append('releaseDate', formState.inputValues.releaseDate);
    formData.append('pages', formState.inputValues.pages);
    formData.append('price', formState.inputValues.price || 0.00);
    formData.append('pdf', formState.inputValues.pdf);
    formData.append('cover', formState.inputValues.cover);
    setLoading(true);

    AxiosInstance({
      method: 'post',
      url: '/book',
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    })
      .then((response) => {
        Swal.fire(t('book.SUCCESS_CREATE_BOOK'), '', 'success');
        resetFormState();
      })
      .catch((error) => {
        if (error.status === 400) {
          handleErrorsServer(error.data.message);
        } else if (error.status === 413) {
          Swal.fire(t('book.ERROR_FILE_TOO_LARGE'), '', 'error');
        } else Swal.fire(t('book.ERROR_CREATE_BOOK'), '', 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const inputValues = {
    ISBN: { value: '', validate: { required: true } },
    authors: { value: '', validate: { required: true } },
    categories: { value: '', validate: { required: true } },
    title: { value: '', validate: { required: true } },
    cover: { value: '', validate: { required: false } },
    pdf: { value: '', validate: { required: true } },
    summary: { value: '', validate: { required: false, max: 500 } },
    abstract: { value: '', validate: { required: false, max: 500 } },
    editionNumber: { value: '', validate: { required: true } },
    publicationNumber: { value: '', validate: { required: true } },
    publishingHouse: { value: '', validate: { required: true } },
    releaseDate: { value: '', validate: { required: true } },
    pages: { value: '', validate: { required: true } },
    price: { value: '0.00', validate: { required: false } },
  };

  const { formState, inputChangeHandler, submitHandler, resetFormState, field, handleErrorsServer } = FormState(
    inputValues,
    createBook
  );

  return {
    formState,
    submitHandler,
    resetFormState,
    inputChangeHandler,
    loading,
    field,
    authors,
    categories,
    createAuthor,
    back,
  };
};

export default useForm;
